import { useEffect, useMemo, useState } from 'react'
import { Modal, Select } from '@/components/ui'
import type { Attendee, ScanPointOption } from '../types'

interface MarkAttendanceModalProps {
  /** Attendee being marked; the modal is open while this is set. */
  attendee: Attendee | null
  scanPoints: ScanPointOption[]
  /** Scan point open right now, preselected when the attendee has no scan there yet. */
  defaultScanPointId?: number | null
  loading?: boolean
  onClose: () => void
  onConfirm: (scanPointId: number) => void
}

/**
 * Lets staff record attendance by hand at a scan point they pick. Points the
 * attendee already has a scan at stay listed but disabled.
 */
export default function MarkAttendanceModal({
  attendee,
  scanPoints,
  defaultScanPointId,
  loading = false,
  onClose,
  onConfirm,
}: MarkAttendanceModalProps) {
  const [scanPointId, setScanPointId] = useState<number | null>(null)

  const markedPointIds = useMemo(
    () => new Set((attendee?.scan_attendances || []).map((scan) => scan.scan_point_id)),
    [attendee]
  )

  useEffect(() => {
    if (!attendee) return

    const available = scanPoints.filter((point) => !markedPointIds.has(point.id))
    const preferred = available.find((point) => point.id === defaultScanPointId) || available[0]
    setScanPointId(preferred?.id ?? null)
  }, [attendee, scanPoints, defaultScanPointId, markedPointIds])

  const options = scanPoints.map((point) => ({
    value: point.id,
    label: markedPointIds.has(point.id) ? `${point.name} (already marked)` : point.name,
    disabled: markedPointIds.has(point.id),
  }))

  return (
    <Modal
      title="Mark Attendance"
      open={Boolean(attendee)}
      onClose={onClose}
      onOk={() => {
        if (scanPointId) onConfirm(scanPointId)
      }}
      okText="Mark Attendance"
      confirmLoading={loading}
      okButtonProps={{ disabled: !scanPointId }}
    >
      <p className="text-sm text-gray-500 mb-4">
        Record attendance for <span className="font-medium text-gray-900">{attendee?.full_name}</span> at
        the selected scan point. The attendee is checked in if not already.
      </p>
      <Select
        label="Scan Point"
        required
        placeholder="Select a scan point"
        value={scanPointId ?? undefined}
        onChange={(value) => setScanPointId(value as number)}
        options={options}
      />
    </Modal>
  )
}
